// agent_id → human-readable PBX host name, everywhere the UI mentions a PBX.
//
// UI convention (docs/UI_GUIDELINES.md): the PBX host is always displayed by
// NAME (e.g. "PBX-01"), never by its agent UUID. This hook fetches the org's
// PBX hosts once (cached 5 min) and returns a lookup that falls back to a
// shortened id ("9082a8ae…") only while loading or for unknown ids.

import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export function usePbxNames() {
  const q = useQuery({
    queryKey: ['pbx-hosts'],
    queryFn: () => api.get('/pbx-hosts'),
    staleTime: 5 * 60_000,
  });

  const byId = useMemo(() => {
    const list = Array.isArray(q.data) ? q.data : (q.data?.hosts || []);
    const map = {};
    for (const h of list) {
      if (h?.agent_id) map[h.agent_id] = h.name || h.agent_id;
    }
    return map;
  }, [q.data]);

  // agent_id → the destination string that host uses for a suspended number
  // (agents.suspended_destination — its NoServiceAnnouncement). Verso writes this
  // itself on suspend, so a number pointing at it is NOT a foreign call flow.
  // Without it the config classifier saw an `app-announcement,…` destination,
  // called it complex, and locked a suspended customer's config read-only — you
  // could not undo the state the product had just put them in (2026-08-02).
  const suspendedDestByHost = useMemo(() => {
    const list = Array.isArray(q.data) ? q.data : (q.data?.hosts || []);
    const map = {};
    for (const h of list) {
      const d = h?.suspended_destination ?? h?.suspendedDestination;
      if (h?.agent_id && d) map[h.agent_id] = String(d).trim();
    }
    return map;
  }, [q.data]);

  // Commio route profile → agent_id. A DID sitting on a host's profile IS being
  // delivered to that host, so this is how the UI names the host of a number whose
  // `pbx_agent_id` was never recorded — instead of showing "—" beside a route that
  // literally spells out the hostname, which is what Stone Mountain Lodge's DIDs tab
  // did. Same derivation the provisioning planner uses.
  const hostByRouteId = useMemo(() => {
    const list = Array.isArray(q.data) ? q.data : (q.data?.hosts || []);
    const map = {};
    for (const h of list) {
      const rid = h?.commio_route_id ?? h?.commioRouteId;
      if (h?.agent_id && rid != null) map[String(rid)] = h.agent_id;
    }
    return map;
  }, [q.data]);

  // pbxName('9082a8ae-…') → 'PBX-01' (or '9082a8ae…' until loaded/unknown).
  const pbxName = (agentId) => {
    if (!agentId) return null;
    return byId[agentId] || `${String(agentId).slice(0, 8)}…`;
  };

  /**
   * The host a DID's calls actually reach: what Verso recorded, else whoever owns
   * its carrier route. `derived: true` says the second one answered, so the UI can
   * show it without claiming Verso knows it.
   */
  // Stable identity: callers put this in useMemo/useEffect dependency arrays, and a
  // fresh function every render makes those recompute on every render.
  const hostForDid = useCallback((did) => {
    const recorded = did?.pbxAgentId ?? did?.pbx_agent_id ?? null;
    if (recorded) return { agentId: recorded, derived: false };
    const rid = did?.commioRouteId ?? did?.commio_route_id ?? null;
    const fromCarrier = rid != null ? hostByRouteId[String(rid)] : null;
    return fromCarrier ? { agentId: fromCarrier, derived: true } : { agentId: null, derived: false };
  }, [hostByRouteId]);

  return { pbxName, byId, hostByRouteId, hostForDid, suspendedDestByHost, isLoading: q.isLoading };
}
